import React, { useEffect, useRef, useState } from 'react';
// TODO: Temporary workaround — required only because `react-geetest-captcha` relies on regenerator runtime.
import 'regenerator-runtime/runtime';
import { RGCaptcha } from 'react-geetest-captcha';
import { useTranslation } from 'react-i18next';
import { LOCAL_STORAGE } from '@constants';
import { useChangePreventer, useRemovalPreventer } from 'utils/hooks/useMutationObserver';
import { CAPTCHA_NAME } from './Captcha.constants';
import { useCaptcha } from './withCaptcha';
import { GeetestSolution } from './Captcha.types';
import useNetwork from './useNetwork';

type GeetestUnprocessedSolution = {
  geetest_challenge: string;
  geetest_validate: string;
  geetest_seccode: string;
};

type CaptchaProps = {
  className?: string;
  onSuccess?: (solution: GeetestSolution) => void;
  warningText?: string;
};

const langMapping: { [key: string]: string } = {
  en: 'en',
  es: 'es',
  pt: 'pt-pt',
};

const Captcha = function ({ className, onSuccess, warningText }: CaptchaProps): JSX.Element | null {
  const {
    captchaConfig,
    captchaErrorCallback,
    captchaIsLoading,
    captchaIsMounted,
    captchaSolution,
    captchaSuccessCallback,
  } = useCaptcha();

  const warningElementId = 'captcha-warning';
  const container = useRef<HTMLDivElement>(null);
  const warningElement = useRef<HTMLDivElement>(null);
  useRemovalPreventer(container, warningElementId);
  useChangePreventer(warningElement);

  const { isOnline } = useNetwork();

  const { t } = useTranslation(['ALERTS', 'STATIC']);

  const userLanguage = langMapping[localStorage.getItem(LOCAL_STORAGE.LANGUAGE) || 'en'];

  const [lang, setLang] = useState(userLanguage);
  const [ready, setReady] = useState(false);

  // Changing the language will throw an `old challenge` error because of the re-rendering.
  // In `handleError`, we reload the widget to show it with the new language.
  useEffect(() => {
    setLang(userLanguage);
  }, [userLanguage]);

  useEffect(() => {
    if (captchaSolution && onSuccess) {
      onSuccess(captchaSolution);
    }
  }, [captchaSolution]);

  // RGCaptcha doesn't return the error
  const handleError = () => {
    if (ready) {
      setReady(false);
    } else {
      // On network error, default to English instead of Chinese
      setLang(langMapping.en);
    }
    captchaErrorCallback();
  };

  const handleSuccess = ({
    geetest_challenge: challenge,
    geetest_validate: validate,
    geetest_seccode: seccode,
  }: GeetestUnprocessedSolution) => {
    const solution = {
      challenge,
      validate,
      seccode,
    };
    captchaSuccessCallback(solution);
  };

  if (!captchaIsMounted || !captchaConfig) {
    return null;
  }

  if (!isOnline) {
    return <span>{t('ALERTS:OFFLINE_CAPTCHA_MESSAGE')}</span>;
  }

  if (captchaIsLoading) {
    return null;
  }

  return (
    <div ref={container} className={className}>
      {warningText && (
        <div
          id={warningElementId}
          ref={warningElement}
          className="alert alert--warning text--sm text--left u-mb-2"
        >
          {warningText}
        </div>
      )}
      {!ready && <div className="text--lg pb-2 text--center">{t('STATIC:CAPTCHA_LOADING')}</div>}
      <RGCaptcha
        name={CAPTCHA_NAME}
        width="100%"
        onReady={() => setReady(true)}
        onSuccess={handleSuccess}
        onError={handleError}
        data={captchaConfig}
        lang={lang}
        product="float"
        style={ready ? undefined : { display: 'none' }}
      />
    </div>
  );
};

Captcha.defaultProps = {
  className: undefined,
  onSuccess: undefined,
  warningText: undefined,
};

export default Captcha;
